import { useState, useEffect } from 'react';
import { Alert, Box, Checkbox, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { SimpleTreeView } from '@mui/x-tree-view';
import { ClearIcon } from '@mui/x-date-pickers';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Icon as Iconify } from '@iconify/react';
import { useParams, useLocation } from "react-router-dom";
import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import { useModuleNavigate } from "../../../layouts/main/ModuleNavigation";
import { useSchemeStore } from '../schemeStore';
import LoadingProgressBar from '../../../components/loading-progressbar/loadingprogressbar';
import schemedefinationService from '../../../services/schemedefination.service';
import { useSchemeSavePermission } from '../utils/useSchemeSavePermission';

export default function SchemeArea() {

    const location = useLocation();
    const isCopyScheme = location.state?.data?.copyScheme;
    const canSaveScheme = useSchemeSavePermission(isCopyScheme);

    const {
        schemeDetails,
        schemeId,
        formData,
        updateScheme,
        tabValue,
        prevTabValue,
        setAreaData,
        addScheme
    } = useSchemeStore();

    const [loading, setLoading] = useState(false);
    const { handleSubmit, setValue, register, control, formState: { errors } } = useForm();
    const { enqueueSnackbar } = useSnackbar();
    const [showErrorMessages, setShowErrorMessages] = useState(false); // NOSONAR
    const { id } = useParams();
    const navigate = useModuleNavigate();


    const [alertType, setAlertType] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);


    const displayAlert = (type, message, show) => {
        setAlertType(type);
        setAlertMessage(message);
        setShowAlert(show);
    }
    const hideAlert = () => {
        setAlertType('');
        setAlertMessage('');
        setShowAlert(false);
    }


    const idKey = location.pathname.includes('schemedefination/newscheme') ? schemeId : id;

    function updatePayload(payload) {
        return {
            SchemeJSON: payload.SchemeJSON.map(scheme => {
                const {
                    FromDate,
                    ToDate,
                    LiftingUOM,
                    SchemeData,
                    Item,
                    Slabs,
                    Target,
                    Point,
                    Retailer,
                    Distributor,
                    Area,
                    ContributionItem,
                    MailCirculation,
                    SchemeDocument
                } = scheme;

                let Filterss = [
                    Item.length > 0 ? Item[0] : undefined,
                    Retailer.length > 0 ? Retailer[0] : undefined,
                    Distributor.length > 0 ? Distributor[0] : undefined, // Check if Distributor array has elements
                    Area, // Check if Area array has elements
                ];

                Filterss = Filterss.filter(filter => filter != null && (!Array.isArray(filter) || filter.length > 0));

                return {
                    FromDate,
                    ToDate,
                    LiftingUOM,
                    Scheme: {
                        Id: idKey,
                        ...SchemeData,
                        Point,
                        Filters: Filterss,
                        Slabs,
                        ContributionItem,
                        Target,
                        MailCirculation,
                        SchemeDocument,
                    },

                };
            })
        };
    }



    function generateDetailsArray(selectedItems, selectedItems2, fullFetchedNodes) {
        const details = {};
        const conditionalStatements = [];

        selectedItems.forEach((item) => {
            const node = fullFetchedNodes.find(nodee => nodee.AreaId === item);
            if (node) {
                const key = `IN:${node.ColumnName}`;
                if (!details[key]) {
                    details[key] = [];
                    conditionalStatements.push(`IN:${node.ColumnName}`);
                }
                details[key].push(item);
            }
        });

        selectedItems2.forEach((item) => {
            const node = fullFetchedNodes.find(nodee => nodee.AreaId === item);
            if (node) {
                const key = `NotIN:${node.ColumnName}`;
                if (!details[key]) {
                    details[key] = [];
                    conditionalStatements.push(`NotIN:${node.ColumnName}`);
                }
                details[key].push(item);
            }
        });

        // If no conditions, return an empty array
        if (conditionalStatements.length === 0) {
            return [];
        }

        const formattedDetails = {};
        let index = 1;

        Object.keys(details).forEach((key) => {
            const [condition, columnName] = key.split(':');
            formattedDetails[`${index}!${columnName}`] = details[key];
            conditionalStatements[conditionalStatements.indexOf(key)] = `${index}!${condition}:${columnName}`;
            // eslint-disable-next-line no-plusplus
            index++;
        });

        const conditionalStatement = `( ${conditionalStatements.join(' AND ')} )`;

        return [
            {
                ...formattedDetails,
                ConditionalStatement: conditionalStatement
            }
        ]
    }

    /* Get All Descendents Code start */

    function findAreaByIdentifier(area, areaId) {

        if (!area) {
            return null;
        }


        if (area?.AreaId === areaId || area?.AreaParentId === areaId) {
            return area;
        }

        if (Array.isArray(area.Areas) && area.Areas.length > 0) {
            const foundArea = area.Areas.map(childArea => findAreaByIdentifier(childArea, areaId))
                .find(result => result !== null);
            return foundArea || null;
        }

        return null;
    }

    function getAllAreaIdentifiers(area) {
        let ids = [];

        // If there are child areas, traverse them recursively
        if (area.Areas && area.Areas.length > 0) {
            area.Areas.forEach(childArea => {
                ids = ids.concat(getAllAreaIdentifiers(childArea));
            });
        }

        // Add the current area's ID after all children
        if (area.AreaId) {
            ids.push(area.AreaId);
        }

        return ids;
    }

    function getDescendantIdentifiers(areaData, targetAreaId) {
        // Find the area by ID
        const targetArea = findAreaByIdentifier(areaData, targetAreaId);

        // If area is found, get all descendant IDs
        if (targetArea) {
            return getAllAreaIdentifiers(targetArea).filter(identifier => identifier !== targetAreaId); // Exclude the target area's ID
        }

        return []; // Return an empty array if no area found with the given ID
    }

    /* Get All Descendents Code end */

    const filterChildren = (fullTreeData, arr) => {
        // Create a new array to hold the filtered IDs
        const filteredArr = [...arr];

        arr.forEach((idd) => {
            // Get all descendant identifiers for the current idd
            const allChilds = getDescendantIdentifiers(fullTreeData, idd);

            // Remove all descendants from the filteredArr
            allChilds.forEach((childId) => {
                const index = filteredArr.indexOf(childId);
                if (index !== -1) {
                    filteredArr.splice(index, 1); // Remove the child ID from the array
                }
            });
        });

        return filteredArr; // Return the filtered array
    }




    const onSubmit = () => {

        setLoading(true);

        try {
            setLoading(true);

            const filtered1 = filterChildren(treeData, selectedItems);
            const filtered2 = filterChildren(treeData, selectedItems2);
            const generatedDetails = generateDetailsArray(filtered1, filtered2, fullFetchedNodes);


            let area;
            if (generatedDetails.length > 0) {
                area = {
                    FilterType: "Area",
                    Details: generatedDetails
                };
            } else {
                area = []; // explicitly pass empty array
            }



            const payload = schemeDetails;

            payload.FromDate = payload.From_FromDate;
            payload.ToDate = payload.To_ToDate;
            payload.From_FromDate = undefined;
            payload.To_ToDate = undefined;

            if (selectedItems.length === 0 && selectedItems2.length === 0) {
                setLoading(false);
                return;
            }
            payload.Area = area;


            const payloadd = {
                "SchemeJSON": [
                    payload
                ]
            }

            const finalPayload = updatePayload(payloadd);

            const formDataa = new FormData();

            formDataa.append('files', null);

            formDataa.append('schemeJSON', JSON.stringify(finalPayload));


            setLoading(true);
            schemedefinationService.then(schemeDefinationInstance => {
                schemeDefinationInstance.UpdateScheme(formData)
                    .then(response => {
                        if (response.status === 200) {
                            setLoading(false);
                            displayAlert('success', response.data.Message, true);
                            setTimeout(() => {
                                hideAlert();
                                navigate('/scheme/scheme-definition');
                            }, 2000);
                        } else {
                            setLoading(false);
                            displayAlert('success', response.data.Message, true);
                            setTimeout(() => {
                                hideAlert();
                            }, 2000);
                        }
                    })
                    .catch(er => {
                        //  console.log("Error Updating Scheme: ", er);
                        setLoading(false);
                    });
            });
            setLoading(false);

        } catch (error) {
            console.error('Error occured: ', error);
            enqueueSnackbar('Error updating scheme', { variant: 'error' });
            setLoading(false);
        }


    }


    /* TREEVIEW BELOW RESTRICTED AREA DO NOT ENTER WITHOUT PERMISSION */

    const [defaultType, setDefaultType] = useState(String(schemeDetails?.Area[0]?.Details[0]?.ConditionalStatement).includes('NotIN') ? 'NotIN' : 'IN');
    const [selectedItems, setSelectedItems] = useState([]); // used for In
    const [selectedItems2, setSelectedItems2] = useState([]); // used for NotIn


    function extractIDs(details) {
        if (!Array.isArray(details) || details.length === 0 || !details[0]) {
            return { inIds: [], notInIds: [] };
        }
        const { ConditionalStatement, ...areaDetails } = details[0];

        const inIdsSet = new Set();
        const notInIdsSet = new Set();

        if (ConditionalStatement) {
            // Regex to extract conditions from the statement (matches any column name including spaces, hyphens, underscores, numbers)
            const inPattern = /(\d+)!IN:([A-Za-z0-9_\-\s]+?)(?=\s+AND|\s+OR|\s*\)|$)/g;
            const notInPattern = /(\d+)!NotIN:([A-Za-z0-9_\-\s]+?)(?=\s+AND|\s+OR|\s*\)|$)/g;

            let match = inPattern.exec(ConditionalStatement);
            while (match !== null) {
                const keyNum = match[1];
                const colName = match[2].trim();
                const exactKey = `${keyNum}!${colName}`;

                if (areaDetails[exactKey] && Array.isArray(areaDetails[exactKey])) {
                    areaDetails[exactKey].forEach(idd => inIdsSet.add(String(idd)));
                } else {
                    Object.keys(areaDetails).forEach(k => {
                        if (k.startsWith(`${keyNum}!`) && Array.isArray(areaDetails[k])) {
                            areaDetails[k].forEach(idd => inIdsSet.add(String(idd)));
                        }
                    });
                }
                match = inPattern.exec(ConditionalStatement);
            }

            let matchNotIn = notInPattern.exec(ConditionalStatement);
            while (matchNotIn !== null) {
                const keyNum = matchNotIn[1];
                const colName = matchNotIn[2].trim();
                const exactKey = `${keyNum}!${colName}`;

                if (areaDetails[exactKey] && Array.isArray(areaDetails[exactKey])) {
                    areaDetails[exactKey].forEach(idd => notInIdsSet.add(String(idd)));
                } else {
                    Object.keys(areaDetails).forEach(k => {
                        if (k.startsWith(`${keyNum}!`) && Array.isArray(areaDetails[k])) {
                            areaDetails[k].forEach(idd => notInIdsSet.add(String(idd)));
                        }
                    });
                }
                matchNotIn = notInPattern.exec(ConditionalStatement);
            }
        }

        // Fallback: If sets are empty but areaDetails contains arrays
        if (inIdsSet.size === 0 && notInIdsSet.size === 0) {
            const isNotIN = String(ConditionalStatement).includes('NotIN');
            Object.values(areaDetails).forEach(val => {
                if (Array.isArray(val)) {
                    val.forEach(idd => {
                        if (isNotIN) notInIdsSet.add(String(idd));
                        else inIdsSet.add(String(idd));
                    });
                }
            });
        }

        const inIds = Array.from(inIdsSet);
        const notInIds = Array.from(notInIdsSet);

        return { inIds, notInIds };
    }

    useEffect(() => {
        try {
            const fetchIds = [];

            const extractedIds = extractIDs(schemeDetails?.Area[0]?.Details);
            const { inIds, notInIds } = extractedIds;

            setSelectedItems(inIds);
            setSelectedItems2(notInIds);

            if (schemeDetails?.Area[0]?.Details) {
                schemeDetails.Area[0].Details.forEach(item => {
                    Object.values(item).forEach(value => {
                        if (Array.isArray(value)) {
                            value.forEach(areaId => {
                                if (areaId) fetchIds.push(String(areaId));
                            });
                        }
                    });
                });
            }

            const uniqueFetchIds = Array.from(new Set(fetchIds));
            if (uniqueFetchIds.length > 0) {
                setLoading(true);
                schemedefinationService.then(schemeDefinationInstance => {
                    schemeDefinationInstance.GetSchemeAreaDetails(uniqueFetchIds.join(','))
                        .then(response => {
                            if (response.status === 200 && Array.isArray(response.data)) {
                                const newNodes = response.data.map((item) => ({
                                    AreaId: String(item.value ?? item.AreaId ?? ''),
                                    AreaName: item.text ?? item.AreaName ?? '',
                                    ColumnName: item.ColumnName,
                                    IsChield: Boolean(item.IsChild ?? item.IsChield),
                                })).filter(n => n.AreaId);

                                setFullFetchedNodes(prevNodes => {
                                    const map = new Map(prevNodes.map(n => [String(n.AreaId), n]));
                                    newNodes.forEach(n => map.set(String(n.AreaId), n));
                                    return Array.from(map.values());
                                });
                            }
                        })
                        .catch(er => {
                            // console.log("Error getting area details: ", er);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                });
            }
        } catch (error) {
            console.error("Error fetching areas: ", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeDetails]);



    const [fetchId, setFetchId] = useState(null);
    const [treeData, setTreeData] = useState(null);
    const [lastSelectedItem, setLastSelectedItem] = useState(null); // For IN
    const [lastSelectedItem2, setLastSelectedItem2] = useState(null); // For NotIN
    const [fetchedNodes, setFetchedNodes] = useState([]);
    const [fullFetchedNodes, setFullFetchedNodes] = useState([{
        "ColumnName": "AreaLevelMaster-COUNTRY",
        "AreaId": "000000000000001",
        "AreaName": "INDIA",
        "IsChield": true
    }]);
    const [expandedItems, setExpandedItems] = useState([]);

    useEffect(() => {
        //  console.log("treedata: ", treeData);
    }, [treeData]);


    const getItemId = (item) => {
        if (!item) return null;
        const val = item.ChildAreaId ??
            item.childAreaId ??
            item.ChildAreaID ??
            item.AreaId ??
            item.areaId ??
            item.id ??
            item.ID ??
            null;
        return val != null ? String(val).trim() : null;
    };

    const getParentId = (item) => {
        if (!item) return null;
        const val = item.ParentAreaId ??
            item.parentAreaId ??
            item.ParentAreaID ??
            item.ParentId ??
            item.parentId ??
            null;
        return val != null ? String(val).trim() : null;
    };

    const getItemName = (item) => {
        if (!item) return '';
        return item.ChildArea ||
            item.childArea ||
            item.ChildAreaName ||
            item.AreaName ||
            item.areaName ||
            item.Area ||
            item.Name ||
            item.name ||
            '';
    };

    const getIsChild = (item) => {
        if (!item) return false;
        return Boolean(
            item.HasChild === true ||
            item.HasChild === 1 ||
            item.HasChild === '1' ||
            item.hasChild === true ||
            item.hasChild === 1 ||
            item.hasChild === '1' ||
            item.IsChield ||
            item.IsChild
        );
    };

    const buildHierarchyFromFlatList = (flatAreas) => {
        if (!Array.isArray(flatAreas) || flatAreas.length === 0) {
            return {
                AreaId: '000000000000001',
                AreaName: 'INDIA',
                AreaParentId: '000000000000001',
                AreaParentName: 'INDIA',
                Level: 1,
                IsChield: true,
                Areas: [],
            };
        }

        // 1. Locate root item (INDIA)
        const rootRow = flatAreas.find(item => {
            const pId = getParentId(item);
            const cId = getItemId(item);
            const name = getItemName(item);
            const level = item?.Level ?? item?.level;
            return level === 1 || level === '1' || pId === '000000000000000' || pId === '0' || pId === 'NA' || cId === '000000000000001' || name?.toUpperCase() === 'INDIA';
        });

        const rootId = rootRow ? getItemId(rootRow) : '000000000000001';
        const rootName = rootRow ? getItemName(rootRow) : 'INDIA';

        // 2. Group items by ParentAreaId
        const childrenByParent = new Map();

        const addChildrenByParent = (parentId, childObj) => {
            if (!parentId) return;
            const pStr = String(parentId).trim();
            const pUnpadded = pStr.replace(/^0+/, '') || '0';

            [pStr, pUnpadded].forEach(key => {
                if (!childrenByParent.has(key)) {
                    childrenByParent.set(key, []);
                }
                const list = childrenByParent.get(key);
                if (!list.some(existing => existing.AreaId === childObj.AreaId)) {
                    list.push(childObj);
                }
            });
        };

        flatAreas.forEach(item => {
            if (!item) return;
            const itemId = getItemId(item);
            if (!itemId) return;

            const name = getItemName(item) || itemId;
            const parentId = getParentId(item);
            const isChild = getIsChild(item);
            const level = item?.Level ?? item?.level ?? null;

            // Skip root item from being added under 000000000000000
            if (itemId === rootId && (!parentId || parentId === '000000000000000' || parentId === '0' || parentId === 'NA')) {
                return;
            }

            const nodeObj = {
                ...item,
                AreaId: itemId,
                AreaName: name,
                ParentId: parentId,
                IsChield: isChild,
                Level: level,
                Areas: [],
            };

            if (parentId) {
                addChildrenByParent(parentId, nodeObj);
            }
        });

        // 3. Recursively construct child branches
        const visitedIds = new Set();

        const buildSubTree = (nodeId) => {
            const strId = String(nodeId).trim();
            const unpaddedId = strId.replace(/^0+/, '') || '0';
            const directChildren = childrenByParent.get(strId) || childrenByParent.get(unpaddedId) || [];

            const assembledChildren = [];
            directChildren.forEach(child => {
                if (visitedIds.has(child.AreaId)) return;
                visitedIds.add(child.AreaId);

                const deepChild = {
                    ...child,
                    Areas: buildSubTree(child.AreaId),
                };
                deepChild.IsChield = deepChild.Areas.length > 0 || deepChild.IsChield;
                assembledChildren.push(deepChild);
            });

            return assembledChildren;
        };

        const rootChildren = buildSubTree(rootId);

        return {
            AreaId: rootId,
            AreaName: rootName,
            AreaParentId: rootId,
            AreaParentName: rootName,
            Level: 1,
            IsChield: true,
            Areas: rootChildren,
        };
    };

    const [nodeLimits, setNodeLimits] = useState({});

    const findNodePathsAndPositions = (rootNode, targetIds) => {
        if (!rootNode || !targetIds || targetIds.length === 0) return { expandedPaths: [], limits: {} };

        const targetSet = new Set(targetIds.map(String));
        const pathsToExpand = new Set();
        const limits = {};

        const traverse = (node, currentPath) => {
            const nodeId = getItemId(node) || String(node.AreaId || node.AreaParentId || '');
            if (!nodeId) return false;
            const thisPath = currentPath ? `${currentPath}/${nodeId}` : nodeId;

            if (Array.isArray(node.Areas) && node.Areas.length > 0) {
                let hasSelectedDescendant = false;
                node.Areas.forEach((child, index) => {
                    const childId = getItemId(child) || String(child.AreaId || '');
                    if (!childId) return;
                    const isSelected = targetSet.has(childId);
                    const childHasSelected = traverse(child, thisPath);

                    if (isSelected || childHasSelected) {
                        hasSelectedDescendant = true;
                        if (index >= (limits[thisPath] || 50)) {
                            limits[thisPath] = Math.ceil((index + 1) / 50) * 50;
                        }
                    }
                });

                if (hasSelectedDescendant) {
                    pathsToExpand.add(thisPath);
                    return true;
                }
            }

            return targetSet.has(nodeId);
        };

        traverse(rootNode, '');

        return {
            expandedPaths: Array.from(pathsToExpand),
            limits
        };
    };

    useEffect(() => {
        if (!treeData) return;
        const currentSelected = defaultType === 'IN' ? selectedItems : selectedItems2;
        const rootPath = String(treeData.AreaId || treeData.AreaParentId || '000000000000001');

        if (currentSelected && currentSelected.length > 0) {
            const { expandedPaths, limits } = findNodePathsAndPositions(treeData, currentSelected);
            setExpandedItems(prev => Array.from(new Set([...prev, rootPath, ...expandedPaths])));
            if (Object.keys(limits).length > 0) {
                setNodeLimits(prev => ({ ...prev, ...limits }));
            }
        } else {
            setExpandedItems(prev => (prev.length > 0 ? prev : [rootPath]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData, selectedItems, selectedItems2, defaultType]);

    useEffect(() => {
        const initializeTree = async () => {
            setLoading(true);
            const fetchData = async (idd) => {
                try {
                    const schemeDefinationInstance = await schemedefinationService;
                    const response = await schemeDefinationInstance.GetArea(idd);
                    return response?.data;
                } catch (error) {
                    console.error('Error fetching tree data:', error);
                    return null;
                }
            };

            try {
                const initialData = await fetchData(fetchId);
                if (initialData) {
                    let flatList = [];
                    if (Array.isArray(initialData)) {
                        flatList = initialData;
                    } else if (Array.isArray(initialData.Areas)) {
                        flatList = initialData.Areas;
                    } else {
                        flatList = [initialData];
                    }

                    const rootTree = buildHierarchyFromFlatList(flatList);
                    setTreeData(rootTree);
                    setFetchedNodes([rootTree.AreaId]);
                    setExpandedItems([rootTree.AreaId]);

                    const newNodes = [];
                    flatList.forEach(item => {
                        const itemId = getItemId(item);
                        const name = getItemName(item);
                        const isChild = getIsChild(item);
                        if (itemId) {
                            newNodes.push({
                                AreaId: itemId,
                                AreaName: name,
                                ColumnName: item.ColumnName || `AreaLevelMaster-${name}`,
                                IsChield: isChild,
                            });
                        }
                    });

                    if (newNodes.length > 0) {
                        setFullFetchedNodes(prev => {
                            const map = new Map(prev.map(n => [String(n.AreaId), n]));
                            newNodes.forEach(n => map.set(String(n.AreaId), n));
                            return Array.from(map.values());
                        });
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        if (fetchId != null) {
            initializeTree();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchId]);

    const getRealAreaId = (nodePath) => {
        if (!nodePath) return '';
        const str = String(nodePath);
        if (str.includes('/')) {
            const parts = str.split('/');
            return parts[parts.length - 1];
        }
        return str;
    };

    const clearSelection = (fieldName) => {
        if (defaultType === 'IN') {
            setSelectedItems([]);
            setLastSelectedItem(null);
        } else {
            setSelectedItems2([]);
            setLastSelectedItem2(null);
        }
        setValue(fieldName, null);
    };

    const findAreaById = (dataa, idd) => {
        if (!dataa || !idd) return null;
        const idStr = String(idd);
        const selfId = getItemId(dataa) || String(dataa.AreaId || dataa.AreaParentId || '');
        if (selfId === idStr) return dataa;
        if (!dataa.Areas || !Array.isArray(dataa.Areas)) return null;
        // eslint-disable-next-line no-restricted-syntax
        for (const area of dataa.Areas) {
            const areaId = getItemId(area) || String(area.AreaId || '');
            if (areaId === idStr) return area;
            const found = findAreaById(area, idStr);
            if (found) return found;
        }
        return null;
    };

    const handleExpandedItemsChange = (event, itemIds) => {
        if (event) {
            event.stopPropagation();
        }
        setExpandedItems(itemIds);
    };

    const handleItemSelectionToggle = (event, nodePath, isSelected, areaObj, controllerField) => {
        if (event) {
            event.stopPropagation();
        }
        const realId = getRealAreaId(nodePath);
        if (!realId) return;

        const itemLabel = areaObj?.AreaName || areaObj?.Area || event?.target?.innerText || realId;
        const treeItem = { AreaId: realId, Area: itemLabel };

        if (defaultType === 'IN') {
            setSelectedItems(prev => {
                let next;
                if (isSelected) {
                    if (prev.some(selectedId => String(selectedId) === String(realId))) {
                        next = prev;
                    } else {
                        next = [...prev, realId];
                    }
                } else {
                    next = prev.filter(selectedId => String(selectedId) !== String(realId));
                }
                setLastSelectedItem(next.length > 0 ? treeItem : null);
                if (controllerField?.onChange) {
                    controllerField.onChange(next);
                }
                return next;
            });
        } else {
            setSelectedItems2(prev => {
                let next;
                if (isSelected) {
                    if (prev.some(selectedId => String(selectedId) === String(realId))) {
                        next = prev;
                    } else {
                        next = [...prev, realId];
                    }
                } else {
                    next = prev.filter(selectedId => String(selectedId) !== String(realId));
                }
                setLastSelectedItem2(next.length > 0 ? treeItem : null);
                if (controllerField?.onChange) {
                    controllerField.onChange(next);
                }
                return next;
            });
        }
    };

    const renderTreeItems = (areas, parentPath, controllerField) => {
        if (!Array.isArray(areas) || areas.length === 0) return null;

        const currentLimit = nodeLimits[parentPath] || 50;
        const visibleAreas = areas.slice(0, currentLimit);
        const hasMore = areas.length > currentLimit;

        return (
            <>
                {visibleAreas.map((area) => {
                    const realAreaId = getItemId(area) || String(area.AreaId || '');
                    if (!realAreaId) return null;
                    const areaDisplayName = getItemName(area) || area.AreaName || realAreaId;
                    const nodePath = `${parentPath}/${realAreaId}`;
                    const hasLoadedChildren = Array.isArray(area.Areas) && area.Areas.length > 0;
                    const isNodeSelected = (defaultType === 'IN' ? selectedItems : selectedItems2).some(selectedId => String(selectedId) === realAreaId);

                    return (
                        <TreeItem
                            key={nodePath}
                            itemId={nodePath}
                            label={
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    py: 0.2,
                                    borderRadius: '4px',
                                    backgroundColor: isNodeSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    px: isNodeSelected ? 0.5 : 0,
                                }}>
                                    <Checkbox
                                        size="small"
                                        checked={isNodeSelected}
                                        sx={{
                                            p: 0,
                                            mr: 0.5,
                                            transform: 'scale(0.8)',
                                            '& .MuiSvgIcon-root': { fontSize: '12px' },
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleItemSelectionToggle(e, nodePath, !isNodeSelected, area, controllerField);
                                        }}
                                    />
                                    <span style={{
                                        fontSize: '14px',
                                        userSelect: 'none',
                                        fontWeight: isNodeSelected ? 600 : 400,
                                        color: isNodeSelected ? '#1976d2' : 'inherit',
                                    }}>
                                        {areaDisplayName}
                                    </span>
                                </Box>
                            }
                        >
                            {hasLoadedChildren ? (
                                renderTreeItems(area.Areas, nodePath, controllerField)
                            ) : null}
                        </TreeItem>
                    );
                })}
                {hasMore && (
                    <TreeItem
                        key={`${parentPath}/loadmore`}
                        itemId={`${parentPath}/loadmore`}
                        label={
                            <span style={{ fontSize: '12px', color: '#1976d2', fontWeight: 600, paddingLeft: '8px' }}>
                                + Load more ({areas.length - currentLimit} remaining)...
                            </span>
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setNodeLimits(prev => ({
                                ...prev,
                                [parentPath]: (prev[parentPath] || 50) + 100
                            }));
                        }}
                    />
                )}
            </>
        );
    };



    useEffect(() => {
        if (prevTabValue.name === 'Area') {

            formDataSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);


    const formDataSave = async () => {
        //  console.log("saveData called");

        try {

            const filtered1 = filterChildren(treeData, selectedItems);
            const filtered2 = filterChildren(treeData, selectedItems2);
            const generatedDetails = generateDetailsArray(filtered1, filtered2, fullFetchedNodes);


            let area;
            if (generatedDetails.length > 0) {
                area = {
                    FilterType: "Area",
                    Details: generatedDetails
                };
            } else {
                area = []; // explicitly pass empty array
            }



            setAreaData(area);




        } catch (err) {
            console.log("Err: ", err);
        }
    }

    async function saveData() {
        //  console.log("saveData called");

        try {
            setLoading(true);

            const filtered1 = filterChildren(treeData, selectedItems);
            const filtered2 = filterChildren(treeData, selectedItems2);
            const generatedDetails = generateDetailsArray(filtered1, filtered2, fullFetchedNodes);

            let area;
            if (generatedDetails.length > 0) {
                area = {
                    FilterType: "Area",
                    Details: generatedDetails
                };
            } else {
                area = []; // explicitly pass empty array
            }

            //  console.log("area Payload: ", area);


            if (selectedItems.length === 0 && selectedItems2.length === 0) {
                try {
                    if (isCopyScheme) {
                        const result = await addScheme(); // NOSONAR
                    } else {

                        const result = await updateScheme(); // NOSONAR
                    }
                } catch (err) {
                    // handle error
                }
                return;
            }



            setAreaData(area);
            try {
                if (isCopyScheme) {
                    const result = await addScheme(); // NOSONAR
                } else {

                    const result = await updateScheme(); // NOSONAR
                }
            } catch (err) {
                // handle error
            }


        } catch (err) {
            //  console.log("Err: ", err);
        } finally {
            setLoading(false);
        }
    }



    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', flexGrow: 1, height: '100%' }}>
                <Box
                    rowGap={2}
                    columnGap={2}
                    display="flex"
                    gap={2}
                    sx={{ mt: 2, flexGrow: 1 }}

                >

                    <FormControl style={{ width: '150px' }} size='small'>
                        <style>
                            {`.css-1d1wxla-MuiFormLabel-root-MuiInputLabel-root.Mui-focused {color: #424242 !important;}`}
                        </style>
                        <InputLabel style={{ fontSize: '14px' }} size="small">Type</InputLabel>
                        <Select
                            size="small"
                            label='Type'
                            defaultValue={defaultType}
                            {...register('filterType', { required: 'Type is required' })}
                            onChange={(e) => {
                                setDefaultType(e.target.value);
                            }}
                            style={{ fontSize: '13px' }}
                        >
                            <MenuItem style={{ fontSize: '13px' }} value='IN'>IN</MenuItem>
                            <MenuItem style={{ fontSize: '13px' }} value='NotIN'>Not IN</MenuItem>
                        </Select>
                        {errors.filterType && <FormHelperText error style={{ color: 'red', fontSize: '10px' }}>{errors.filterType.message}</FormHelperText>}
                    </FormControl>


                    <FormControl variant="outlined" size="small" sx={{ width: '350px' }}>

                        <Controller
                            name='Area'
                            doit={fetchId === null ? setFetchId('000000000000001') : () => { }}  // NOSONAR
                            // defaultValue={(pageType === 'edit') ? { AreaId: field.DefaultValues[0]?.AreaId, Area: field.DefaultValues[0]?.AreaName } : null}
                            control={control}
                            render={({ field: controllerField }) => (  // NOSONAR 
                                <FormControl fullWidth variant="outlined" size="small" >
                                    <style>
                                        {
                                            ` .css-gescg-MuiTreeItem-content .MuiTreeItem-label {
                                                                    font-size: 13px;
                                                                } `
                                        }
                                    </style>
                                    <InputLabel
                                        size='small'
                                        sx={{
                                            '&.Mui-focused': {
                                                color: '#424242',
                                            },
                                        }}
                                        style={{ fontSize: '14px' }}
                                    >
                                        Area
                                    </InputLabel>
                                    <Select
                                        value={defaultType === 'IN' ?
                                            selectedItems.map(item => {
                                                const node = fullFetchedNodes.find(nodee => String(nodee.AreaId) === String(item));
                                                return node ? node.AreaName : item;
                                            }) :
                                            selectedItems2.map(item => {
                                                const node = fullFetchedNodes.find(nodee => String(nodee.AreaId) === String(item));
                                                return node ? node.AreaName : item;
                                            })
                                        }
                                        label="Area"
                                        size="small"
                                        style={{ fontSize: '13px' }}
                                        defaultValue={defaultType}
                                        multiple
                                        onChange={(e) => {
                                            controllerField.onChange(e.target.value);
                                        }}

                                        renderValue={() => {
                                            const currentSelected = defaultType === 'IN' ? selectedItems : selectedItems2;
                                            if (!currentSelected || currentSelected.length === 0) return 'Select an area';
                                            const displayValue = currentSelected.map(item => {
                                                const node = fullFetchedNodes.find(nodee => String(nodee.AreaId) === String(item));
                                                let name = node?.AreaName;
                                                if (!name && treeData) {
                                                    const foundInTree = findAreaById(treeData, item);
                                                    name = getItemName(foundInTree) || foundInTree?.AreaName;
                                                }
                                                return name || String(item);
                                            }).filter(Boolean).join(', ');

                                            const displayValueShortened = displayValue.length > 35 ? `${displayValue.slice(0, 35)}...` : displayValue;

                                            return (
                                                <Tooltip
                                                    title={
                                                        <div
                                                            style={{
                                                                maxHeight: '150px',
                                                                overflowY: 'auto',
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    color: '#9e9e9e',
                                                                    fontSize: '12px',
                                                                    fontWeight: '300',
                                                                }}
                                                            >
                                                                {displayValue}
                                                            </span>
                                                        </div>
                                                    }
                                                    arrow
                                                    placement="top"
                                                    PopperProps={{
                                                        sx: {
                                                            '& .MuiTooltip-tooltip': {
                                                                backgroundColor: 'white',
                                                                color: 'black',
                                                                border: '1px solid #ccc',
                                                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 4px rgba(0, 0, 0, 0.25)',
                                                            },
                                                            '& .MuiTooltip-arrow': {
                                                                color: 'white',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {displayValueShortened}
                                                    </Box>
                                                </Tooltip>
                                            );
                                        }}
                                        sx={{
                                            width: '100%', // Set a fixed width
                                            '& .MuiSelect-root': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }
                                        }}
                                        endAdornment={
                                            (defaultType === 'IN' ? selectedItems.length > 0 : selectedItems2.length > 0) && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="clear selection"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            clearSelection('Area');
                                                            controllerField.onChange(null); // Update form state on clear
                                                        }}
                                                        edge="end"
                                                        size="small"
                                                        sx={{ marginRight: '18px', p: 0.25 }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: '275px',
                                                    overflowY: 'auto',
                                                    fontSize: '13px',
                                                },
                                            },
                                        }}
                                    >

                                        {treeData && (
                                            <SimpleTreeView
                                                expandedItems={expandedItems}
                                                onExpandedItemsChange={handleExpandedItemsChange}
                                                aria-label="icon expansion"
                                                expansionTrigger="iconContainer"
                                                sx={{
                                                    p: 0.5,
                                                    '& .MuiTreeItem-groupTransition': {
                                                        marginLeft: '14px !important',
                                                        paddingLeft: '6px !important',
                                                        borderLeft: '1px dashed #d0d7de',
                                                    },
                                                    '& .MuiTreeItem-content': {
                                                        padding: '2px 4px !important',
                                                        borderRadius: '4px',
                                                        '&:hover': {
                                                            backgroundColor: '#f6f8fa',
                                                        },
                                                    },
                                                    '& .MuiTreeItem-label': {
                                                        fontSize: '14px',
                                                    },
                                                }}
                                            >
                                                <TreeItem
                                                    key={String(treeData?.AreaId || treeData?.AreaParentId)}
                                                    itemId={String(treeData?.AreaId || treeData?.AreaParentId)}
                                                    label={(() => {
                                                        const rootId = String(treeData?.AreaId || treeData?.AreaParentId);
                                                        const isRootSelected = (defaultType === 'IN' ? selectedItems : selectedItems2).some(selectedId => String(selectedId) === rootId);
                                                        return (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                py: 0.2,
                                                                borderRadius: '4px',
                                                                backgroundColor: isRootSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                                                px: isRootSelected ? 0.5 : 0,
                                                            }}>
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={isRootSelected}
                                                                    sx={{
                                                                        p: 0,
                                                                        mr: 0.5,
                                                                        transform: 'scale(0.8)',
                                                                        '& .MuiSvgIcon-root': { fontSize: '12px' },
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleItemSelectionToggle(e, rootId, !isRootSelected, { AreaId: rootId, AreaName: treeData?.AreaName || treeData?.AreaParentName, IsChield: true }, controllerField);
                                                                    }}
                                                                />
                                                                <span style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    userSelect: 'none',
                                                                    color: isRootSelected ? '#1976d2' : 'inherit',
                                                                }}>
                                                                    {treeData?.AreaName || treeData?.AreaParentName}
                                                                </span>
                                                            </Box>
                                                        );
                                                    })()}
                                                >
                                                    {treeData.Areas && renderTreeItems(treeData.Areas, String(treeData?.AreaId || treeData?.AreaParentId), controllerField)}
                                                </TreeItem>
                                            </SimpleTreeView>
                                        )}
                                    </Select>
                                    {showErrorMessages && !selectedItems.length > 0 && <FormHelperText error sx={{ color: 'red' }}>Area is required</FormHelperText>}

                                </FormControl>


                            )}

                        />


                    </FormControl>

                </Box>
                <Box sx={{ mt: 1 }}>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        alignItems="center"
                        sx={{ padding: '2px' }}
                    >
                        {schemeDetails?.SchemeLock !== true && canSaveScheme && (
                            <Tooltip title="Save">
                                <IconButton type="button" onClick={() => saveData()} sx={{
                                    bgcolor: '#43b5a2',
                                    color: '#fff',
                                    borderRadius: '4px',
                                    height: '30px',
                                    width: '31px',
                                    padding: '4px',
                                }}>
                                    <Iconify icon="bx:save" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>


            </form>
            {showAlert && (
                <Stack sx={{ width: '30%', position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -40%)" }} spacing={2}>
                    <Alert
                        severity={alertType}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setAlertType('');
                                    setAlertMessage('');
                                    setShowAlert(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {alertMessage}
                    </Alert>
                </Stack>
            )}
            {
                loading && (
                    <LoadingProgressBar />
                )
            }
        </div>
    )
}
